// frontend/src/components/staff/MissingPunchSection.js
//
// Workforce — TIME-010 §3 Staff-side Submit Missing Punch surface.
//
// Renders ABOVE the clock-in card on /staff/clock per Zuri spec §3.1:
//   - "My Pending Corrections" inline list (rendered when non-empty)
//   - [Submit Missing Punch] entry-point button (always visible)
//
// On submit, POSTs to /workforce/timelog-corrections per Marcus tech doc §4.1.
// On success, the new request appears in the list with `[Pending]` chip.
// Polls every 30s to surface admin Approve/Reject decisions (Zuri §3.2 —
// the visual flip IS the notification mechanism in v1; no push/SMS).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { timelogCorrectionAPI, jobCodeAPI } from "../../services/api";
import JobCodePicker from "./JobCodePicker";
import "./MissingPunchSection.css";

const STUDIO_TZ = "America/Phoenix";
const POLL_INTERVAL_MS = 30_000;
const REASON_MIN = 10;
const REASON_MAX = 500;

const STATUS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  WITHDRAWN: "Withdrawn",
};

const STATUS_META = {
  [STATUS.PENDING]: { label: "Pending", className: "mps-pill-pending" },
  [STATUS.APPROVED]: { label: "Approved", className: "mps-pill-approved" },
  [STATUS.REJECTED]: { label: "Rejected", className: "mps-pill-rejected" },
  [STATUS.WITHDRAWN]: { label: "Withdrawn", className: "mps-pill-withdrawn" },
};

const phxPartsToIso = (date, time) => {
  if (!date || !time) return null;
  const m = moment.tz(`${date}T${time}`, STUDIO_TZ);
  if (!m.isValid()) return null;
  return m.toISOString();
};

const todayPhxIsoDate = () => moment.tz(STUDIO_TZ).format("YYYY-MM-DD");

const formatStudioDateTime = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return `${m.format("MMM D, h:mm A")} MST`;
};

const formatStudioDate = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return m.format("MMM D, YYYY");
};

const formatTimeRange = (inIso, outIso) => {
  if (!inIso) return "—";
  const inLabel = moment.tz(inIso, STUDIO_TZ).format("h:mm A");
  const outLabel = outIso
    ? moment.tz(outIso, STUDIO_TZ).format("h:mm A")
    : "(still working)";
  return `${inLabel} – ${outLabel} MST`;
};

const MissingPunchModal = ({ open, onCancel, onSuccess }) => {
  const [draft, setDraft] = useState({
    date: todayPhxIsoDate(),
    inTime: "",
    outTime: "",
    jobCode: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [bannerError, setBannerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  // TIME-006 — replace the free-text jobCode input with the picker. Fetch the
  // active list when the modal opens; cache for the lifetime of the modal.
  const [jobCodes, setJobCodes] = useState([]);
  const [jobCodesLoading, setJobCodesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft({
        date: todayPhxIsoDate(),
        inTime: "",
        outTime: "",
        jobCode: "",
        reason: "",
      });
      setSubmitting(false);
      setBannerError(null);
      setFieldErrors({});
    }
  }, [open]);

  // Fetch the active job-code list when the modal opens. Empty list -> the
  // picker omits itself entirely (emptyState="skip" — design spec §3.2).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setJobCodesLoading(true);
    jobCodeAPI
      .list({ active: true })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.jobCodes || res?.data || [];
        setJobCodes(Array.isArray(list) ? list : []);
        setJobCodesLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Per spec §2.3: fetch failure does not block the punch — the
        // submission falls back to jobCode: null and the server records the
        // primaryRole. Mirror the silent-fall-back posture from the legacy
        // free-text path.
        setJobCodes([]);
        setJobCodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const trimmedReason = draft.reason.trim();
  const reasonValid =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;

  const today = todayPhxIsoDate();
  const dateValid = Boolean(draft.date) && draft.date <= today;

  const inIso = phxPartsToIso(draft.date, draft.inTime);
  const outIso = phxPartsToIso(draft.date, draft.outTime);
  const timeRangeValid =
    Boolean(inIso) &&
    (!outIso || new Date(outIso).getTime() > new Date(inIso).getTime());

  const canSubmit =
    !submitting && reasonValid && dateValid && timeRangeValid && Boolean(inIso);

  const mapErr = (err) => {
    const data = err?.response?.data || {};
    const code = data.error || "server_error";
    switch (code) {
      case "reason_too_short":
        return { surface: "field", key: "reason", msg: `At least ${REASON_MIN} characters required.` };
      case "reason_too_long":
        return { surface: "field", key: "reason", msg: `At most ${REASON_MAX} characters.` };
      case "invalid_target_date":
        return { surface: "field", key: "date", msg: "Invalid date." };
      case "invalid_intended_punch_in_time":
        return { surface: "field", key: "inTime", msg: "Invalid punch-in time." };
      case "invalid_time_range":
        return { surface: "field", key: "outTime", msg: "Punch-out must be after punch-in." };
      case "invalid_job_code":
        return {
          surface: "field",
          key: "jobCode",
          msg: "That job code is no longer active. Pick a different one.",
        };
      case "period_locked":
        return {
          surface: "banner",
          accent: "locked",
          msg: "That date is in a locked pay period and cannot accept corrections.",
        };
      case "staff_profile_not_found":
        return {
          surface: "banner",
          accent: "error",
          msg: "Your staff profile was not found. Contact an admin.",
        };
      default:
        return {
          surface: "banner",
          accent: "error",
          msg: "Couldn't submit your request. Try again, or check your connection.",
        };
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setBannerError(null);
    setFieldErrors({});
    try {
      await timelogCorrectionAPI.submit({
        targetTimeLogId: null,
        targetDate: draft.date,
        intendedPunchInTime: inIso,
        intendedPunchOutTime: outIso,
        intendedJobCode: (draft.jobCode || "").trim() || null,
        reason: trimmedReason,
      });
      setSubmitting(false);
      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      const m = mapErr(err);
      setSubmitting(false);
      if (m.surface === "field") {
        setFieldErrors((prev) => ({ ...prev, [m.key]: m.msg }));
      } else {
        setBannerError({ message: m.msg, accent: m.accent });
      }
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="mps-backdrop" aria-hidden="true" />
      <div
        className="mps-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mps-modal-heading"
        data-testid="mps-modal"
      >
        <header className="mps-modal-header">
          <h2 id="mps-modal-heading" className="mps-modal-heading">
            Submit Missing Punch
          </h2>
          <p className="mps-modal-subhead">
            Tell your admin which shift you missed punching for. They&apos;ll
            review and either approve or reject.
          </p>
        </header>
        {bannerError && (
          <div
            className={`mps-banner mps-banner-${bannerError.accent || "error"}`}
            role="alert"
          >
            {bannerError.message}
          </div>
        )}
        <div className="mps-modal-body">
          <div className="mps-field">
            <label className="mps-label" htmlFor="mps-date">
              Date <span className="mps-required">*</span>
            </label>
            <input
              id="mps-date"
              type="date"
              value={draft.date}
              max={today}
              onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
              className="mps-input"
              data-testid="mps-date"
            />
            {!dateValid && (
              <p className="mps-field-error" role="alert">
                Date cannot be in the future.
              </p>
            )}
            {fieldErrors.date && (
              <p className="mps-field-error" role="alert">
                {fieldErrors.date}
              </p>
            )}
          </div>
          <div className="mps-field-pair">
            <div className="mps-field">
              <label className="mps-label" htmlFor="mps-in-time">
                Punch in <span className="mps-required">*</span>
              </label>
              <input
                id="mps-in-time"
                type="time"
                value={draft.inTime}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, inTime: e.target.value }))
                }
                className="mps-input"
                data-testid="mps-in-time"
              />
              {fieldErrors.inTime && (
                <p className="mps-field-error" role="alert">
                  {fieldErrors.inTime}
                </p>
              )}
            </div>
            <div className="mps-field">
              <label className="mps-label" htmlFor="mps-out-time">
                Punch out
              </label>
              <input
                id="mps-out-time"
                type="time"
                value={draft.outTime}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, outTime: e.target.value }))
                }
                className="mps-input"
                data-testid="mps-out-time"
              />
              <p className="mps-helper">Leave blank if you&apos;re still working.</p>
              {fieldErrors.outTime && (
                <p className="mps-field-error" role="alert">
                  {fieldErrors.outTime}
                </p>
              )}
            </div>
          </div>
          <p className="mps-helper">All times are Phoenix time (MST).</p>
          {/* TIME-006 §3.2 — picker replaces the free-text input. The free-
              text affordance is gone (no path for staff to invent a slug).
              When the org has zero active codes, the picker omits itself
              entirely via emptyState="skip" so the form has no empty slot. */}
          {(jobCodesLoading || jobCodes.length > 0) && (
            <div className="mps-field">
              <label className="mps-label" htmlFor="mps-job-code">
                Job code
              </label>
              <JobCodePicker
                inputId="mps-job-code"
                testId="mps-job-code"
                size="md"
                codes={jobCodes}
                loading={jobCodesLoading}
                value={draft.jobCode || null}
                onChange={(code) =>
                  setDraft((d) => ({ ...d, jobCode: code || "" }))
                }
                placeholder="(use my primary role)"
                emptyState="skip"
                errorMessage={fieldErrors.jobCode || undefined}
              />
            </div>
          )}
          <div className="mps-field">
            <label className="mps-label" htmlFor="mps-reason">
              Reason <span className="mps-required">*</span>
            </label>
            <textarea
              id="mps-reason"
              className="mps-textarea"
              value={draft.reason}
              onChange={(e) =>
                setDraft((d) => ({ ...d, reason: e.target.value }))
              }
              maxLength={REASON_MAX + 100}
              placeholder="Forgot to punch — was at front desk 8am-2pm covering for ..."
              data-testid="mps-reason"
              aria-required="true"
            />
            <div className="mps-counter-row">
              <span className="mps-helper">
                Required. At least {REASON_MIN} characters.
              </span>
              <span
                className={
                  trimmedReason.length > REASON_MAX
                    ? "mps-counter mps-counter-over"
                    : "mps-counter"
                }
                aria-live="polite"
              >
                {trimmedReason.length} / {REASON_MAX}
              </span>
            </div>
            {fieldErrors.reason && (
              <p className="mps-field-error" role="alert">
                {fieldErrors.reason}
              </p>
            )}
          </div>
        </div>
        <footer className="mps-modal-footer">
          <button
            type="button"
            className="mps-btn mps-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="mps-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="mps-btn mps-btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="mps-submit"
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </footer>
      </div>
    </>
  );
};

const MissingPunchSection = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [successToast, setSuccessToast] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const intervalRef = useRef(null);

  const fetchOwn = useCallback(async () => {
    try {
      const res = await timelogCorrectionAPI.list({});
      const list = res?.data?.corrections || [];
      // Server scopes to own-only when staff role per Marcus §4.2.
      setRequests(Array.isArray(list) ? list : []);
    } catch (e) {
      // Silent — keep last known list.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOwn();
    intervalRef.current = setInterval(fetchOwn, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchOwn]);

  const sorted = useMemo(
    () =>
      [...requests].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [requests]
  );

  const handleSubmitSuccess = () => {
    setModalOpen(false);
    setSuccessToast({
      kind: "success",
      message: "Correction request submitted. Roderick will review.",
    });
    fetchOwn();
    setTimeout(() => setSuccessToast(null), 4000);
  };

  return (
    <section
      className="mps-section"
      aria-labelledby="mps-section-heading"
      data-testid="mps-section"
    >
      {sorted.length > 0 && !loading && (
        <div className="mps-list">
          <h2 id="mps-section-heading" className="mps-list-heading">
            My Pending Corrections
          </h2>
          <ul className="mps-list-ul">
            {sorted.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META[STATUS.PENDING];
              const isExpanded = expandedRow === r._id;
              return (
                <li className="mps-list-row" key={r._id}>
                  <div className="mps-list-row-top">
                    <span className={`mps-pill ${meta.className}`}>
                      <span aria-hidden="true" className="mps-pill-dot" />
                      {meta.label}
                    </span>
                    <span className="mps-list-row-date">
                      {formatStudioDate(r.targetDate)}
                    </span>
                    <span className="mps-list-row-times">
                      {formatTimeRange(
                        r.intendedPunchInTime,
                        r.intendedPunchOutTime
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="mps-list-row-reason"
                    onClick={() =>
                      setExpandedRow(isExpanded ? null : r._id)
                    }
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? r.reason : `${r.reason.slice(0, 80)}${r.reason.length > 80 ? "…" : ""}`}
                  </button>
                  {r.decisionReason && (
                    <p
                      className={`mps-decision-note mps-decision-note-${r.status?.toLowerCase()}`}
                    >
                      <strong>Decision note:</strong> {r.decisionReason}
                    </p>
                  )}
                  {r.status === STATUS.APPROVED && r.resultingTimeLogId && (
                    <p className="mps-decision-cta">
                      <a href={`#timelog-${r.resultingTimeLogId}`}>
                        View corrected timesheet entry &rarr;
                      </a>
                    </p>
                  )}
                  <p className="mps-list-row-meta">
                    Submitted {formatStudioDateTime(r.createdAt)}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mps-cta-row">
        <button
          type="button"
          className="mps-btn mps-btn-secondary mps-cta-btn"
          onClick={() => setModalOpen(true)}
          data-testid="mps-open-modal"
        >
          Submit Missing Punch
        </button>
      </div>

      <MissingPunchModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onSuccess={handleSubmitSuccess}
      />

      {successToast && (
        <div
          className={`mps-toast mps-toast-${successToast.kind}`}
          role="status"
          data-testid="mps-toast"
          onClick={() => setSuccessToast(null)}
        >
          {successToast.message}
        </div>
      )}
    </section>
  );
};

export default MissingPunchSection;
